import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Star, Trash2, Upload, ArrowLeft } from "react-feather";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Spinner,
} from "reactstrap";
import {
  getSystemProductListing,
  getSyetmProductById,
  getSystemProductCategoryListing,
  updateSystemProduct,
  uploadSystemProductImages,
  deleteSystemProductImage,
  setMainSystemProductImage,
} from "../../../utils/apiCalls";
import { FALLBACK_IMAGE } from "../../../utils/imageUtils";
import { getProductOptions, getProductAttributes } from "../../../utils/productAttributesApi";
import MultiSelectField from "../../common/MultiSelectField";
import BoxSlotsEditor, { slotsFromProduct, slotsToPayload } from "./box-slots-editor";
import { C, reportingGlobalCSS } from "../../reporting/reportingStyles";
import Breadcrumb from "../../common/breadcrumb";
import { useSelector } from "react-redux";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};


// Themes this page's reactstrap cards/inputs/buttons to the reporting
// (devices-list) look without restructuring the markup.
const pageCSS = `
  .rpt-wrap .card { border-radius: 14px; border: 1px solid ${C.border}; box-shadow: ${C.shadow}; margin-bottom: 20px; }
  .rpt-wrap .card-header { background: ${C.white}; border-bottom: 2px solid ${C.borderLight}; border-radius: 14px 14px 0 0 !important; padding: 16px 24px; }
  .rpt-wrap .card-header h5 { font-size: 14px; font-weight: 700; color: ${C.text}; margin: 0; }
  .rpt-wrap .card-body { padding: 22px 24px; }
  .rpt-wrap label { font-weight: 600; font-size: 13px; color: ${C.text}; }
  .rpt-wrap .form-control, .rpt-wrap .form-select, .rpt-wrap select, .rpt-wrap input[type="text"], .rpt-wrap input[type="number"] { border-radius: 10px; border: 1.5px solid ${C.border}; font-size: 13px; }
  .rpt-wrap .btn-primary { background: ${C.coral} !important; border: none !important; border-radius: 10px; font-weight: 600; font-size: 13px; }
  .rpt-wrap .btn-primary:hover { background: ${C.coralDark} !important; }
  .rpt-wrap .btn-link { color: ${C.coral}; font-weight: 600; text-decoration: none; }
  .rpt-wrap .btn-secondary { border-radius: 10px; font-weight: 600; font-size: 13px; }
`;

const EditSystemProduct = () => {
  const navigate = useNavigate();
  const { productId } = useParams();

  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);

  const [form, setForm] = useState({
    title: "",
    arabicName: "",
    description: "",
    arabicDescription: "",
    price: 0,
    sortOrder: 0,
    is_active: true,
    bestSeller: false,
    SystemProductCategoryId: "",
    size: "",
    grind: "",
    altitude: "",
    variety: "",
    process: "",
    flavorNotes: "",
    olderPrice: "",
    country: "",
    roastType: "",
    coffeeKindId: "",
    fruitinessLevelId: "",
    flavorFamilyId: "",
    textureId: "",
    roastLevelId: "",
    flavorNoteOptionIds: [],
    // purchase options: [{valueId, priceExtra, isDefault}]
    attributeValues: [],
    brewingSuggestions: "",
    brewingMethods: [],
  });

  // Options master data (quiz kinds), grouped by kind for the selects below.
  const [optionsByKind, setOptionsByKind] = useState({});
  // Purchase-option attributes (Size/Grind) with their values.
  const [attributes, setAttributes] = useState([]);

  // Build-your-own boxes: their slots, and the products a slot may offer.
  const isBox = product?.productClass === "bundle";
  const [slots, setSlots] = useState([]);
  const [slotCandidates, setSlotCandidates] = useState([]);

  const fetchProduct = async () => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    setIsLoading(true);
    //const products = await getSystemProductListing(selectedMerchant, {});
    //const found = (products || []).find((p) => p.id === parseInt(productId));
    const found = await getSyetmProductById(productId);
    if (found) {
      setProduct(found);
      setForm({
        title: found.title || "",
        arabicName: found.arabicName || "",
        description: found.description || "",
        arabicDescription: found.arabicDescription || "",
        price: found.price || 0,
        sortOrder: found.sortOrder || 0,
        is_active: found.is_active,
        bestSeller: found.bestSeller,
        SystemProductCategoryId: found.SystemProductCategoryId || "",
        size: found.size || "",
        grind: found.grind || "",
        altitude: found.altitude || "",
        variety: found.variety || "",
        process: found.process || "",
        flavorNotes: found.flavorNotes || "",
        olderPrice: found.olderPrice || "",
        country: found.country || "",
        roastType: found.roastType || "",
        coffeeKindId: found.coffeeKindId || "",
        fruitinessLevelId: found.fruitinessLevelId || "",
        flavorFamilyId: found.flavorFamilyId || "",
        textureId: found.textureId || "",
        roastLevelId: found.roastLevelId || "",
        flavorNoteOptionIds: (found.flavorNoteOptions || []).map((o) => o.id),
        attributeValues: (found.attributes || []).flatMap((attribute) =>
          attribute.values.map((v) => ({
            valueId: v.id,
            priceExtra: v.priceExtra || 0,
            isDefault: !!v.isDefault,
          }))
        ),
        brewingSuggestions: found.brewingSuggestions || "",
        // A box has no brewing guide; sending the merchant defaults would create rows for it.
        brewingMethods: found.productClass === "bundle" ? [] : found.brewingMethods || [],
      });
      setSlots(slotsFromProduct(found.selectionGroups));
    }
    setIsLoading(false);
  };

  // Slot candidates: this merchant's Odoo products. A box's contents go to Odoo line by
  // line, so a product with no Odoo id (or another box) can never be inside one.
  useEffect(() => {
    const loadCandidates = async () => {
      if (!isBox || !selectedMerchant || selectedMerchant === "all") return;
      const products = await getSystemProductListing(selectedMerchant, { productClass: "product" });
      setSlotCandidates((products || []).filter((p) => p.merchant_product_id));
    };
    loadCandidates();
  }, [isBox, selectedMerchant]);

  useEffect(() => {
    fetchProduct();
  }, [productId, selectedMerchant]);

  useEffect(() => {
    const loadOptions = async () => {
      if (!selectedMerchant || selectedMerchant === "all") return;
      const options = await getProductOptions({ merchantId: selectedMerchant, isActive: true });
      const grouped = {};
      (options || []).forEach((option) => {
        if (!grouped[option.kind]) grouped[option.kind] = [];
        grouped[option.kind].push(option);
      });
      setOptionsByKind(grouped);

      const attrs = await getProductAttributes({ merchantId: selectedMerchant });
      setAttributes(attrs || []);
    };
    loadOptions();
  }, [selectedMerchant]);

  // ---- Purchase Options helpers -------------------------------------------
  const getAttachment = (valueId) => form.attributeValues.find((v) => v.valueId === valueId);

  const toggleAttributeValue = (valueId) => {
    setForm((prev) => {
      const exists = prev.attributeValues.some((v) => v.valueId === valueId);
      return {
        ...prev,
        attributeValues: exists
          ? prev.attributeValues.filter((v) => v.valueId !== valueId)
          : [...prev.attributeValues, { valueId, priceExtra: 0, isDefault: false }],
      };
    });
  };

  const setAttachmentPriceExtra = (valueId, priceExtra) => {
    setForm((prev) => ({
      ...prev,
      attributeValues: prev.attributeValues.map((v) =>
        v.valueId === valueId ? { ...v, priceExtra } : v
      ),
    }));
  };

  // One default per attribute group: clearing the others in the same group.
  const setAttachmentDefault = (attribute, valueId) => {
    const groupValueIds = new Set(attribute.values.map((v) => v.id));
    setForm((prev) => ({
      ...prev,
      attributeValues: prev.attributeValues.map((v) => ({
        ...v,
        isDefault: groupValueIds.has(v.valueId) ? v.valueId === valueId : v.isDefault,
      })),
    }));
  };

  useEffect(() => {
    const loadCategories = async () => {
      if (!selectedMerchant || selectedMerchant === "all") return;
      const cats = await getSystemProductCategoryListing(selectedMerchant);
      setCategories(cats || []);
    };
    loadCategories();
  }, [selectedMerchant]);

  const handleSave = async () => {
    setIsSaving(true);
    const payload = isBox
      ? { ...form, attributeValues: [], selectionGroups: slotsToPayload(slots) }
      : form;
    const result = await updateSystemProduct(parseInt(productId), payload);
    setIsSaving(false);
    if (result) {
      fetchProduct();
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setIsUploadingImages(true);
    await uploadSystemProductImages(parseInt(productId), files);
    setIsUploadingImages(false);
    fetchProduct();
    e.target.value = "";
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    const result = await deleteSystemProductImage(imageId);
    if (result) fetchProduct();
  };

  const handleSetMainImage = async (imageId) => {
    const result = await setMainSystemProductImage(imageId);
    if (result) fetchProduct();
  };

  const updateBrewingMethod = (key, field, value) => {
    setForm(prev => ({
      ...prev,
      brewingMethods: prev.brewingMethods.map(method =>
        method.type === key
          ? { ...method, [field]: value }
          : method
      ),
    }));
  };

  if (isLoading) {
    return (
      <Fragment>
        <Breadcrumb title="Edit System Product" parent="Products" />
        <style>{reportingGlobalCSS + pageCSS}</style>
        <Container fluid className="rpt-wrap">
          <div className="text-center py-5">Loading...</div>
        </Container>
      </Fragment>
    );
  }

  if (!product) {
    return (
      <Fragment>
        <Breadcrumb title="Edit System Product" parent="Products" />
        <style>{reportingGlobalCSS + pageCSS}</style>
        <Container fluid className="rpt-wrap">
          <div className="text-center py-5 text-muted">Product not found.</div>
        </Container>
      </Fragment>
    );
  }

  const images = product.SystemProductImages || [];

  return (
    <Fragment>
      <Breadcrumb title="Edit System Product" parent="Products" />
      <style>{reportingGlobalCSS + pageCSS}</style>
      <Container fluid className="rpt-wrap">
        <Row>
          <Col sm="12">
            <Button
              color="link"
              className="mb-3 p-0"
              onClick={() => navigate("/products/system-product-list")}
            >
              <ArrowLeft size={16} className="me-1" /> Back to List
            </Button>
          </Col>
        </Row>

        {/* Basic Info */}
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <h5>
                  Product Details
                  {isBox && <Badge color="warning" className="ms-2">Box</Badge>}
                </h5>
                {isBox && (
                  <small style={{ color: "#6b7280" }}>
                    This box exists only on the website, not in Odoo. Its price is set here; when it is
                    ordered, the products inside it are sent to Odoo as separate lines.
                  </small>
                )}
              </CardHeader>
              <CardBody>
                <Form>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Title</Label>
                        <Input
                          type="text"
                          value={form.title}
                          onChange={(e) => setForm({ ...form, title: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Arabic Name</Label>
                        <Input
                          type="text"
                          value={form.arabicName}
                          onChange={(e) => setForm({ ...form, arabicName: e.target.value })}
                          dir="rtl"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="3">
                      <FormGroup>
                        <Label>{isBox ? "Box Price" : "Price"}</Label>
                        <Input
                          type="number"
                          value={form.price}
                          onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Sort Order</Label>
                        <Input
                          type="number"
                          value={form.sortOrder}
                          onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Status</Label>
                        <Input
                          type="select"
                          value={form.is_active ? "true" : "false"}
                          onChange={(e) => setForm({ ...form, is_active: e.target.value === "true" })}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </Input>
                        {isBox && (
                          <small style={{ color: "#6b7280" }}>
                            Can only be activated once every slot has products.
                          </small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Best Seller</Label>
                        <Input
                          type="select"
                          value={form.bestSeller ? "true" : "false"}
                          onChange={(e) => setForm({ ...form, bestSeller: e.target.value === "true" })}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Category</Label>
                        <Input
                          type="select"
                          value={form.SystemProductCategoryId}
                          onChange={(e) => setForm({ ...form, SystemProductCategoryId: e.target.value || null })}
                        >
                          <option value="">Unassigned</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Box contents — the slots a customer fills */}
        {isBox && (
          <Row>
            <Col sm="12">
              <Card>
                <CardHeader>
                  <h5>Box Contents</h5>
                  <small style={{ color: "#6b7280" }}>
                    Each slot is one choice the customer makes. Price Extra is added to the box price
                    when that product is picked (e.g. a premium coffee = +10). Only products synced
                    from Odoo can go in a box.
                  </small>
                </CardHeader>
                <CardBody>
                  <BoxSlotsEditor slots={slots} onChange={setSlots} candidates={slotCandidates} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Description English */}
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <h5>Description (English)</h5>
              </CardHeader>
              <CardBody>
                <ReactQuill
                  theme="snow"
                  value={form.description}
                  onChange={(value) => setForm({ ...form, description: value })}
                  modules={quillModules}
                  style={{ minHeight: "150px" }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Description Arabic */}
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <h5>Description (Arabic)</h5>
              </CardHeader>
              <CardBody>
                <div dir="rtl">
                  <ReactQuill
                    theme="snow"
                    value={form.arabicDescription}
                    onChange={(value) => setForm({ ...form, arabicDescription: value })}
                    modules={quillModules}
                    style={{ minHeight: "150px" }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Enabled these fields only for Peacock — coffee details, so never for a box */}
        {String(selectedMerchant) === "12" && !isBox && (
          <>
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader>
                    <h5>Purchase Options</h5>
                    <small style={{ color: "#6b7280" }}>
                      Which sizes/grinds this product is sold in. Price Extra is added on top
                      of the base price (e.g. 1kg = +37). Manage the lists in Products → Product
                      Master Data → Attributes.
                    </small>
                  </CardHeader>
                  <CardBody>
                    {attributes.length === 0 ? (
                      <div style={{ color: "#6b7280" }}>
                        No attributes defined yet. Create "Size" / "Grind" in Product Master Data first.
                      </div>
                    ) : (
                      attributes.map((attribute) => (
                        <div key={attribute.id} style={{ marginBottom: "20px" }}>
                          <Label style={{ fontWeight: 600 }}>{attribute.name}</Label>
                          {attribute.values.map((value) => {
                            const attachment = getAttachment(value.id);
                            return (
                              <Row key={value.id} className="align-items-center" style={{ marginBottom: "6px", marginLeft: "4px" }}>
                                <Col md="3">
                                  <Label check style={{ cursor: "pointer" }}>
                                    <Input
                                      type="checkbox"
                                      checked={!!attachment}
                                      onChange={() => toggleAttributeValue(value.id)}
                                      style={{ marginRight: "8px" }}
                                    />
                                    {value.name}
                                  </Label>
                                </Col>
                                {attachment && (
                                  <>
                                    <Col md="3">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        placeholder="Price Extra"
                                        value={attachment.priceExtra}
                                        onChange={(e) =>
                                          setAttachmentPriceExtra(
                                            value.id,
                                            e.target.value === "" ? "" : parseFloat(e.target.value) || 0
                                          )
                                        }
                                      />
                                    </Col>
                                    <Col md="3">
                                      <Label check style={{ cursor: "pointer", color: "#6b7280" }}>
                                        <Input
                                          type="radio"
                                          name={`default-${attribute.id}`}
                                          checked={!!attachment.isDefault}
                                          onChange={() => setAttachmentDefault(attribute, value.id)}
                                          style={{ marginRight: "6px" }}
                                        />
                                        Default
                                      </Label>
                                    </Col>
                                  </>
                                )}
                              </Row>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader>
                    <h5>Coffee Details</h5>
                  </CardHeader>

                  <CardBody>

                    <Row>

                      <Col md="4">
                        <FormGroup>
                          <Label>Altitude</Label>
                          <Input
                            value={form.altitude}
                            onChange={(e)=>setForm({...form,altitude:e.target.value})}
                          />
                        </FormGroup>
                      </Col>

                    </Row>

                    <Row>                      

                      <Col md="4">
                        <FormGroup>
                          <Label>Variety</Label>
                          <Input
                            value={form.variety}
                            onChange={(e)=>setForm({...form,variety:e.target.value})}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label>Process</Label>
                          <Input
                            value={form.process}
                            onChange={(e)=>setForm({...form,process:e.target.value})}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label>Flavor Notes</Label>
                          <MultiSelectField
                            options={(optionsByKind.flavorNote || []).map((option) => ({
                              value: option.id,
                              label: option.name,
                            }))}
                            value={(optionsByKind.flavorNote || [])
                              .filter((option) => form.flavorNoteOptionIds.includes(option.id))
                              .map((option) => ({ value: option.id, label: option.name }))}
                            onChange={(selected) =>
                              setForm({
                                ...form,
                                flavorNoteOptionIds: (selected || []).map((s) => s.value),
                              })
                            }
                            placeholder="Select flavour notes..."
                          />
                        </FormGroup>
                      </Col>

                    </Row>

                    <Row>

                      <Col md="4">
                        <FormGroup>
                          <Label>Old Price (shown as crossed-out price on product page)</Label>
                          <Input
                            value={form.olderPrice}
                            onChange={(e)=>setForm({...form,olderPrice:e.target.value})}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label>Country</Label>
                          <Input
                            value={form.country}
                            onChange={(e)=>setForm({...form,country:e.target.value})}
                          />
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label>Roast Level</Label>
                          <Input
                            type="select"
                            value={form.roastLevelId || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                roastLevelId: e.target.value ? parseInt(e.target.value) : "",
                              })
                            }
                          >
                            <option value="">Select Roast Level</option>
                            {(optionsByKind.roastLevel || []).map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>

                    </Row>


                    <Row>                    

                      <Col md="4">
                        <FormGroup>
                          <Label>Coffee Kind</Label>
                          <Input
                            type="select"
                            value={form.coffeeKindId || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                coffeeKindId: e.target.value ? parseInt(e.target.value) : "",
                              })
                            }
                          >
                            <option value="">Select</option>
                            {(optionsByKind.coffeeKind || []).map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label>Fruitiness Level</Label>
                          <Input
                            type="select"
                            value={form.fruitinessLevelId || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                fruitinessLevelId: e.target.value ? parseInt(e.target.value) : "",
                              })
                            }
                          >
                            <option value="">Select</option>
                            {(optionsByKind.fruitinessLevel || []).map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col md="4">
                        <FormGroup>
                          <Label>Flavor Family</Label>
                          <Input
                            type="select"
                            value={form.flavorFamilyId || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                flavorFamilyId: e.target.value ? parseInt(e.target.value) : "",
                              })
                            }
                          >
                            <option value="">Select</option>
                            {(optionsByKind.flavorFamily || []).map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>

                    </Row>


                    <Row>

                      <Col md="4">
                        <FormGroup>
                          <Label>Texture</Label>
                          <Input
                            type="select"
                            value={form.textureId || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                textureId: e.target.value ? parseInt(e.target.value) : "",
                              })
                            }
                          >
                            <option value="">Select</option>
                            {(optionsByKind.texture || []).map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>

                    </Row>


                    <hr/>

                    <Row>
                      <Col md="12">
                        <FormGroup>
                          <Label>Brewing Suggestions</Label>
                          <ReactQuill
                              theme="snow"
                              value={form.brewingSuggestions}
                              onChange={(value) => setForm({ ...form, brewingSuggestions: value })}
                              modules={quillModules}
                              style={{ minHeight: "250px" }}
                            />
                        </FormGroup>
                      </Col>
                    </Row>
                    
                      
                    <CardHeader>
                      <h5>Brewing Method Details</h5>
                    </CardHeader>
                    
                    {form.brewingMethods?.map((method) => (
                
                        <Col sm="12">
                          <Card>
                            <CardHeader>
                              <h5>{method.type}</h5>
                            </CardHeader>

                            <CardBody>
                              <Row>
                                <Col md="6">
                                  <FormGroup>
                                    <Label>Coffee</Label>
                                    <Input
                                      type="text"
                                      value={method.coffee || ""}
                                      onChange={(e) =>
                                        updateBrewingMethod(
                                          method.type,
                                          "coffee",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </FormGroup>
                                </Col>

                                <Col md="6">
                                  <FormGroup>
                                    <Label>Water</Label>
                                    <Input
                                      type="text"
                                      value={method.water || ""}
                                      onChange={(e) =>
                                        updateBrewingMethod(
                                          method.type,
                                          "water",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </FormGroup>
                                </Col>

                                <Col md="4">
                                  <FormGroup>
                                    <Label>Grind</Label>
                                    <Input
                                      type="text"
                                      value={method.grind || ""}
                                      onChange={(e) =>
                                        updateBrewingMethod(
                                          method.type,
                                          "grind",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </FormGroup>
                                </Col>

                                <Col md="4">
                                  <FormGroup>
                                    <Label>Ratio</Label>
                                    <Input
                                      type="text"
                                      value={method.ratio || ""}
                                      onChange={(e) =>
                                        updateBrewingMethod(
                                          method.type,
                                          "ratio",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </FormGroup>
                                </Col>

                                <Col md="4">
                                  <FormGroup>
                                    <Label>Time</Label>
                                    <Input
                                      type="text"
                                      value={method.time || ""}
                                      onChange={(e) =>
                                        updateBrewingMethod(
                                          method.type,
                                          "time",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </FormGroup>
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>
                        </Col>        
                    ))}                    
                  </CardBody>
                </Card>
              </Col>

            </Row>
          </>
        )}  

        {/* Images */}
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5>Product Images</h5>
                <div>
                  <Label
                    htmlFor="product-image-upload"
                    className="btn btn-primary btn-sm mb-0"
                    style={{ cursor: "pointer" }}
                  >
                    {isUploadingImages ? (
                      <><Spinner size="sm" className="me-1" /> Uploading...</>
                    ) : (
                      <><Upload size={14} className="me-1" /> Upload Images</>
                    )}
                  </Label>
                  <Input
                    id="product-image-upload"
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={isUploadingImages}
                    style={{ display: "none" }}
                  />
                </div>
              </CardHeader>
              <CardBody>
                {images.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No images uploaded yet.
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-3">
                    {images.map((img) => (
                      <div
                        key={img.id}
                        style={{
                          position: "relative",
                          border: img.is_main ? "3px solid #009689" : "2px solid #dee2e6",
                          borderRadius: "8px",
                          padding: "4px",
                        }}
                      >
                        <img
                          src={process.env.REACT_APP_URL + img.image}
                          alt=""
                          onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                          style={{
                            width: "150px",
                            height: "150px",
                            objectFit: "cover",
                            borderRadius: "6px",
                            display: "block",
                          }}
                        />
                        <div className="d-flex justify-content-center gap-1 mt-2">
                          {!img.is_main && (
                            <Button
                              color="info"
                              size="sm"
                              title="Set as main image"
                              onClick={() => handleSetMainImage(img.id)}
                            >
                              <Star size={12} className="me-1" /> Main
                            </Button>
                          )}
                          <Button
                            color="danger"
                            size="sm"
                            title="Delete image"
                            onClick={() => handleDeleteImage(img.id)}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                        {img.is_main && (
                          <Badge
                            color="danger"
                            style={{
                              position: "absolute",
                              top: "-8px",
                              right: "-8px",
                              fontSize: "0.7rem",
                            }}
                          >
                            Main
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Save Button */}
        <Row className="mb-5">
          <Col sm="12" className="d-flex justify-content-end gap-2">
            <Button
              color="secondary"
              onClick={() => navigate("/products/system-product-list")}
            >
              Cancel
            </Button>
            <Button color="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <><Spinner size="sm" className="me-1" /> Saving...</>
              ) : (
                "Save Changes"
              )}
            </Button>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default EditSystemProduct;
